import { Box, Fade, Modal, Tooltip } from '@mui/material'
import React, { useEffect, useRef } from 'react'
import { grey } from '../../colors'
import { createPortal } from 'react-dom'
import { GettingStartedPage } from './WizardPages/GettingStartedPage'
import theme from '../../theme'
import { WizardHeader } from './WizardHeader'
import BackArrow from '@mui/icons-material/ArrowBackIosNew'
import ForwardArrow from '@mui/icons-material/ArrowForwardIos'
import { UploadPeopleAndCoursesPage } from './WizardPages/UploadPeopleAndCoursesPage'
import { CreateSurveyTemplatePage } from './WizardPages/CreateSurveyTemplatePage'
import { SurveyProjectSetupPage } from './WizardPages/SurveyProjectSetupPage'
import { PreviewInfoPage } from './WizardPages/PreviewInfoPage'
import { SetupTermPage } from './WizardPages/TermSetupPage'
import { setLocallyStoredCurrentPage, setLocallyStoredIsLastPageSeeable } from './helper'
import { CongratulationsPage } from './WizardPages/CongratulationsPage'
import { useSetupTour } from './SetupWizardProvider'
import { useLocation } from 'react-router-dom'
import { PreviewInfoPageSteps, SetupWizardSteps, SetupWizardTitles } from './constants'
import { StyledForwardArrowTooltip, StyledIconContainer, StyledModalContainer, StyledNavigationContainer, StyledPageContainer } from './styles'

export const SetupTourFeature: React.FC = (): JSX.Element | null => {
  const {
    isWizardOpen, setIsWizardOpen,
    currPage, setCurrPage,
    isLastPageSeeable, setIsLastPageSeeable,
    newlyAddedTermsToProject, setNewlyAddedTermsToProject
  } = useSetupTour()
  const tourRoot = document.getElementById('tour-root')
  const location = useLocation()
  const prevLocationRef = useRef(location.pathname)
  const numOfPages = Object.keys(SetupWizardSteps).length / 2

  // exits out of wizard if the user navigates to different page
  useEffect(() => {
    const unlisten = (): void => {
      const prev = prevLocationRef.current
      const curr = location.pathname
      if (prev !== curr) {
        setIsWizardOpen(false)
      }
      prevLocationRef.current = curr
    }

    unlisten()
  }, [location])

  // locally store current page index
  useEffect(() => { if (currPage !== SetupWizardSteps.GETTING_STARTED) setLocallyStoredCurrentPage(currPage) }, [currPage])

  // locally store last page seeable state
  useEffect(() => { if (currPage !== SetupWizardSteps.GETTING_STARTED) setLocallyStoredIsLastPageSeeable(isLastPageSeeable) }, [isLastPageSeeable])

  if (!tourRoot || !isWizardOpen) return null

  // define the page to render based on the current page index
  const pageToRender = (pageIdx: number): JSX.Element | null => {
    switch (pageIdx) {
      case SetupWizardSteps.GETTING_STARTED:
        return <GettingStartedPage key='getting-started' setCurrPage={setCurrPage} />
      case SetupWizardSteps.PREVIEW_INFO_PEOPLE_AND_COURSES:
        return <PreviewInfoPage step={PreviewInfoPageSteps.PEOPLE_AND_COURSES} key='preview-info-people-and-courses' />
      case SetupWizardSteps.UPLOAD_PEOPLE_AND_COURSES:
        return <UploadPeopleAndCoursesPage key='upload-people-and-courses' />
      case SetupWizardSteps.PREVIEW_INFO_TERMS:
        return <PreviewInfoPage step={PreviewInfoPageSteps.TERMS} key='preview-info-terms' />
      case SetupWizardSteps.SETUP_TERM:
        return <SetupTermPage key='create-term' />
      case SetupWizardSteps.PREVIEW_INFO_SURVEY_TEMPLATE:
        return <PreviewInfoPage step={PreviewInfoPageSteps.SURVEY_TEMPLATE} key='preview-info-survey-template' />
      case SetupWizardSteps.CREATE_SURVEY_TEMPLATE:
        return <CreateSurveyTemplatePage key='create-survey-template' setIsWizardOpen={setIsWizardOpen} />
      case SetupWizardSteps.PREVIEW_INFO_SURVEY_PROJECT:
        return <PreviewInfoPage step={PreviewInfoPageSteps.SURVEY_PROJECT} key='preview-info-survey-project' />
      case SetupWizardSteps.SURVEY_PROJECT_SETUP:
        return <SurveyProjectSetupPage key='survey-project-setup' setNewlyAddedTermsToProject={setNewlyAddedTermsToProject} setIsLastPageSeeable={setIsLastPageSeeable} />
      case SetupWizardSteps.FINISHED:
        return <CongratulationsPage key='almost-there' setCurrPage={setCurrPage} setIsWizardOpen={setIsWizardOpen} newlyAddedTermsToProject={newlyAddedTermsToProject} />
      default: // should not be reachable
        return null
    }
  }

  // handling for backward and forward arrows
  const handleBackArrowClick = (): void => {
    setCurrPage(Math.max(currPage - 1, 0))
  }
  const handleForwardArrowClick = (): void => {
    if (isForwardArrowUseable) {
      setCurrPage(Math.min(currPage + 1, numOfPages - 1))
    }
  }

  const isForwardArrowUseable = (currPage !== numOfPages - 2) || newlyAddedTermsToProject.length > 0
  const backArrowVisibility = (currPage !== SetupWizardSteps.GETTING_STARTED && currPage !== SetupWizardSteps.PREVIEW_INFO_PEOPLE_AND_COURSES) ? 'visible' : 'hidden'
  const forwardArrowVisibility = (currPage !== SetupWizardSteps.GETTING_STARTED && currPage !== SetupWizardSteps.FINISHED) ? 'visible' : 'hidden'
  const forwardArrowOpacity = isForwardArrowUseable ? 1 : 0.5
  return (
    createPortal(
      <Modal
        open={isWizardOpen}
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}
      >
        <StyledModalContainer theme={theme}>
          {/* Header for the wizard */}
          <WizardHeader title={SetupWizardTitles[currPage]} setIsOpen={setIsWizardOpen} />

          {/* Page content that changes based on the current page index */}
          <Fade in timeout={600} key={currPage}>
            <StyledPageContainer theme={theme}>{pageToRender(currPage)}</StyledPageContainer>
          </Fade>

          {/* Navigation arrows at the bottom of the modal */}
          <StyledNavigationContainer theme={theme}>
            <StyledIconContainer visibility={backArrowVisibility} opacity={1} onClick={handleBackArrowClick}>
              <BackArrow />
            </StyledIconContainer>

            <StyledForwardArrowTooltip title='You must add at least one new term to continue' disableHoverListener={isForwardArrowUseable} placement='top' arrow={true}>
              <StyledIconContainer visibility={forwardArrowVisibility} opacity={forwardArrowOpacity} onClick={handleForwardArrowClick}>
                <ForwardArrow />
              </StyledIconContainer>
            </StyledForwardArrowTooltip>

          </StyledNavigationContainer>
        </StyledModalContainer>
      </Modal>,
      tourRoot
    )
  )
}
